import React, { ReactNode, useState } from 'react'

import Box from 'components_new/atoms/Box'
import CustomIcon from 'components_new/atoms/CustomIcon'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import NavigationItem from 'components_new/molecules/NavigationItem'

import GlobalSearch from 'components_new/organisms/GlobalSearch'
import GroupsNavigation from 'components_new/organisms/GroupsNavigation'
import HelpButton from 'components_new/organisms/HelpButton'
import InternalNavigation from 'components_new/organisms/InternalNavigation'
import ProfileAvatar from 'components_new/organisms/ProfileAvatar'
import ResourcesNavigation from 'components_new/organisms/ResourcesNavigation'
import ResultsNavigation from 'components_new/organisms/ResultsNavigation'
import TablesNavigation from 'components_new/organisms/TablesNavigation'

import LogotypeIcon from 'assets/logotypes/LogotypeIcon'

import { useNavigationActions } from 'redux/actions/NavigationMenu'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useNavigation } from 'redux/hooks/NavigationMenuStore'
import { useCustomer } from 'redux/hooks/Customer'
import { useTranslation } from 'language/translator'
import { useHistory } from 'react-router-dom'

import {
  NAVBAR_COLLAPSED_TOOLTIP_OFFSET,
  NAVBAR_SCROLL_AS_ONE_MEDIA_QUERY
} from './lib'

import GlobalFilter from '../GlobalFilter'
import {
  useAllowAccessExportTables,
  useAllowAccessAnalyses
} from 'redux/hooks/Permissions'
import { useCustomization } from 'redux/hooks/Customization'

interface NavbarProps {
  footer?: ReactNode
}

const Navbar = (props: NavbarProps) => {
  const { footer } = props

  const { open } = useNavigation()

  const { t } = useTranslation()
  const {
    allowAccess,
    isAccessHub,
    isAdmin,
    isCompanyGroup,
    isEconomist,
    isHomepal,
    account
  } = useAccountSettings()
  const customer = useCustomer()
  const { toggleNavigationOpen } = useNavigationActions()
  const history = useHistory()
  const customization = useCustomization()
  const allowAccessExportTables = useAllowAccessExportTables()
  const allowAnalyses = useAllowAccessAnalyses()
  const [searchOpen, setSearchOpen] = useState<boolean>(false)

  if (isAccessHub) {
    return (
      <Box
        sx={{
          height: '100%',
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          minHeight: 0
        }}
      >
        <Box
          sx={{
            flex: 1,
            py: 2,
            px: open ? 2 : '13px',
            overflowY: 'auto'
          }}
        >
          <NavigationItem
            collapsed={!open}
            item={{
              activePattern: '/access-hub*',
              icon: { name: 'ApiOutlined' },
              href: '/access-hub',
              title: t('global', 'api')
            }}
            onToggle={() => {}}
          />
        </Box>
        <Footer open={open} footer={footer} logotype={customization.logotype} />
      </Box>
    )
  }

  const showAccessHub =
    customer?.allow_access.access_hub && (isAdmin || isAccessHub)

  return (
    <>
      <Box
        sx={{
          height: '100%',
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          overflowY: 'auto',
          minHeight: 0,
          px: 1
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.default',
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            [NAVBAR_SCROLL_AS_ONE_MEDIA_QUERY]: {
              position: 'static'
            }
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              ml: -0.25,
              position: 'relative',
              pt: 0.75,
              width: '100%'
            }}
          >
            <Box
              sx={{
                ['&:hover']: {
                  ['.LogotypeIcon']: {
                    opacity: open ? 1 : 0
                  },
                  ['.SidebarIcon']: {
                    opacity: open ? 0 : 1
                  }
                }
              }}
            >
              <IconButton
                onClick={() => {
                  if (open) {
                    history.push('/')
                  } else {
                    toggleNavigationOpen()
                  }
                }}
                size="small"
                sx={{ position: 'relative' }}
              >
                <Box
                  className="LogotypeIcon"
                  sx={{
                    height: '32px',
                    left: -1,
                    position: 'relative',
                    width: '32px'
                  }}
                >
                  <LogotypeIcon />
                </Box>
                <Tooltip
                  placement={!open ? 'right' : undefined}
                  slotProps={NAVBAR_COLLAPSED_TOOLTIP_OFFSET}
                  title={open ? undefined : t('global', 'open_side_panel')}
                >
                  <Box
                    className="SidebarIcon"
                    sx={{
                      alignItems: 'center',
                      bottom: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      left: 0,
                      opacity: 0,
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      zIndex: 1
                    }}
                  >
                    <CustomIcon name="LeftSidebar" />
                  </Box>
                </Tooltip>
              </IconButton>
            </Box>

            <Box
              sx={{
                opacity: open ? 1 : 0,
                transition: 'opacity 0.5s ease'
              }}
            >
              <Tooltip title={t('global', 'close_side_panel')}>
                <IconButton onClick={() => toggleNavigationOpen()}>
                  <CustomIcon name="LeftSidebar" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {account ? (
            <GlobalFilter
              collapsed={!open}
              showCurrencyConverter={allowAccess.allow_currency_converter}
              sx={{ mb: 2 }}
            />
          ) : null}

          <Box
            sx={{
              pb: 2
            }}
          >
            <NavigationItem
              collapsed={!open}
              item={{
                activePattern: '/',
                icon: { name: 'Home' },
                href: '/',
                title: t('global', 'home')
              }}
            />
            <NavigationItem
              active={searchOpen}
              className="SearchNavigationItem"
              collapsed={!open}
              item={{
                icon: { name: 'Search' },
                onClick: () => setSearchOpen(true),
                title: t('global', 'search')
              }}
              shortCutKey="K"
            />
            {customer?.allow_access.allow_key_metrics && !isCompanyGroup ? (
              <NavigationItem
                collapsed={!open}
                item={{
                  activePattern: '/key-metrics*',
                  icon: { name: 'KeyMetrics' },
                  href: '/key-metrics',
                  title: t('key_metrics', 'key_metrics')
                }}
              />
            ) : null}
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            py: 2
          }}
        >
          {isCompanyGroup ? null : <GroupsNavigation />}
          <ResultsNavigation />
          <ResourcesNavigation />

          {allowAccessExportTables ? <TablesNavigation /> : null}

          {isHomepal ? <InternalNavigation /> : null}
        </Box>
        <Box
          sx={{
            bgcolor: 'background.default',
            borderTop: '1px solid',
            borderColor: 'divider',
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            position: 'sticky',
            py: 1,
            zIndex: 1,
            [NAVBAR_SCROLL_AS_ONE_MEDIA_QUERY]: {
              position: 'static'
            }
          }}
        >
          {isAdmin ||
          isEconomist ||
          showAccessHub ||
          allowAnalyses ||
          (customer?.allow_access.allow_key_metrics && !isCompanyGroup) ? (
                <Box
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 1,
                    mb: 1
                  }}
                >
                  {isAdmin ||
              (customer?.allow_access.allow_key_metrics && !isCompanyGroup) ? (
                        <NavigationItem
                          item={{
                            activePattern: '/metrics*',
                            id: 'metrics-all',
                            icon: { name: 'Metrics' } as const,
                            href: '/metrics',
                            title: t('metrics', 'metrics')
                          }}
                        />
                      ) : null}
                  {isAdmin ? (
                    <NavigationItem
                      item={{
                        activePattern: '/entities*',
                        id: 'entities-all',
                        icon: { name: 'Entities' } as const,
                        href: '/entities',
                        title: t('entities', 'entities')
                      }}
                    />
                  ) : null}
                  {allowAnalyses ? (
                    <NavigationItem
                      item={{
                        activePattern: '/analysis-types*',
                        id: 'analyses-all',
                        icon: { name: 'AnalysisTypes' } as const,
                        href: '/analysis-types',
                        title: t('analysis_types', 'analysis_types')
                      }}
                    />
                  ) : null}

                  {showAccessHub ? (
                    <NavigationItem
                      collapsed={!open}
                      item={{
                        icon: { name: 'ApiOutlined' },
                        href: '/access-hub',
                        title: t('global', 'api')
                      }}
                      onToggle={() => {}}
                    />
                  ) : null}

                  {isAdmin || isEconomist ? (
                    <NavigationItem
                      collapsed={!open}
                      item={{
                        activePattern: '/settings*',
                        icon: { name: 'Settings' },
                        href: '/settings',
                        title: t('global', 'settings')
                      }}
                    />
                  ) : null}
                </Box>
              ) : null}

          <Footer
            open={open}
            footer={footer}
            logotype={customization.logotype}
          />
        </Box>
      </Box>

      <GlobalSearch
        onClose={() => {
          setSearchOpen(false)
        }}
        onOpen={() => {
          setSearchOpen(true)
        }}
        open={searchOpen}
      />
    </>
  )
}

const Footer = (props: {
  open: boolean
  logotype: string | null
  footer?: ReactNode
}) => {
  const customer = useCustomer()
  const { isCompanyGroup, isHomepal } = useAccountSettings()

  const { t } = useTranslation()

  return (
    <>
      {props.footer ? (
        <Box
          sx={{
            px: 0.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25
          }}
        >
          {props.footer}
        </Box>
      ) : null}
      {props.logotype && isCompanyGroup ? (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Box sx={{ height: 52 }}>
              <img
                src={props.logotype}
                style={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
            {props.open && customer ? (
              <Text variant="body2" color="text.secondary">
                {t('users', 'logged_in_via_{{name}}', false, {
                  name: customer.name
                })}
              </Text>
            ) : null}
          </Box>
          <Divider sx={{ mb: 1, mt: 1 }} />
        </>
      ) : null}

      {customer && !isCompanyGroup && isHomepal && props.open ? (
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'action.hover',
            borderRadius: 1,
            display: 'flex',
            gap: 0.75,
            mb: 0.5,
            px: 1,
            py: 0.75
          }}
        >
          <Icon color="disabled" fontSize="small" name="ApartmentOutlined" />
          <Text color="text.secondary" noWrap variant="caption">
            {customer.name}
          </Text>
        </Box>
      ) : null}
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex'
        }}
      >
        <ProfileAvatar />
        {props.open ? (
          <Box sx={{ pr: 2 }}>
            <HelpButton />
          </Box>
        ) : null}
      </Box>
    </>
  )
}

export default Navbar
